import { useMemo } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { DotsThree as DotsThreeIcon } from '@phosphor-icons/react/dist/ssr/DotsThree'

import type { AssistantThread } from '@/lib/assistant-api'
import { dayjs } from '@/lib/dayjs'

function formatThreadTimestamp(thread: AssistantThread): string {
	const date = dayjs(thread.updated_at ?? thread.created_at)
	return dayjs().diff(date, 'day') >= 7 ? date.format('MMM D') : date.fromNow()
}

function groupThreads(threads: AssistantThread[]): { label: string; threads: AssistantThread[] }[] {
	const groups: { label: string; threads: AssistantThread[] }[] = []
	const today = dayjs().startOf('day')
	for (const thread of threads) {
		const daysAgo = today.diff(dayjs(thread.updated_at ?? thread.created_at).startOf('day'), 'day')
		const label = daysAgo <= 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : daysAgo <= 7 ? 'Previous 7 days' : 'Older'
		const group = groups.find((g) => g.label === label)
		if (group) group.threads.push(thread)
		else groups.push({ label, threads: [thread] })
	}
	const order = ['Today', 'Yesterday', 'Previous 7 days', 'Older']
	return groups.sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label))
}

export interface ChatHistoryListProps {
	threads: AssistantThread[]
	currentThreadId: string | null
	expanded: boolean
	menuThreadId: string | null
	onOpenMenu: (anchorEl: HTMLElement, threadId: string) => void
	onSelect: (threadId: string) => void
}

export function ChatHistoryList({
	threads,
	currentThreadId,
	expanded,
	menuThreadId,
	onOpenMenu,
	onSelect,
}: ChatHistoryListProps) {
	const groupedThreads = useMemo(() => groupThreads(threads), [threads])

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				overflowY: 'auto',
				overscrollBehavior: 'contain',
				// Clearance under the floating composer + soft bottom fade.
				pb: { xs: '200px', sm: '140px' },
				maskImage: 'linear-gradient(to bottom, black calc(100% - 48px), transparent)',
				WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 48px), transparent)',
			}}
		>
			<List disablePadding sx={expanded ? { maxWidth: 760, mx: 'auto' } : undefined}>
				{groupedThreads.length === 0 ? (
					<Typography color="text.secondary" sx={{ px: 2, py: 3 }} variant="body2">
						No past conversations yet.
					</Typography>
				) : (
					groupedThreads.map((group) => (
						<Box key={group.label} component="li" sx={{ listStyle: 'none' }}>
							<Typography color="text.primary" sx={{ px: 2.5, pt: 6, pb: 2, fontWeight: 600 }} variant="caption">
								{group.label}
							</Typography>
							{group.threads.map((thread) => (
								<ListItemButton
									key={thread.id}
									onClick={() => {
										onSelect(thread.id)
									}}
									selected={thread.id === currentThreadId}
									sx={{
										alignItems: 'center',
										borderRadius: 2.5,
										gap: 1,
										mb: 0.25,
										mx: 1,
										px: 1.5,
										py: 1,
										'&.Mui-selected': {
											bgcolor: 'action.selected',
											'&:hover': { bgcolor: 'action.selected' },
										},
										// Hover-reveal actions on pointer devices; touch
										// devices have no hover so it stays visible there.
										'@media (hover: hover)': {
											'& .thread-menu-btn': { opacity: 0 },
											'&:hover .thread-menu-btn, &:focus-within .thread-menu-btn, & .thread-menu-btn[aria-expanded="true"]':
												{
													opacity: 1,
												},
										},
									}}
								>
									<ListItemText
										primary={thread.title}
										primaryTypographyProps={{
											noWrap: true,
											sx: { fontSize: '0.8125rem', fontWeight: 500, letterSpacing: '-0.01em' },
											variant: 'body2',
										}}
										secondary={formatThreadTimestamp(thread)}
										secondaryTypographyProps={{
											color: 'text.secondary',
											noWrap: true,
											variant: 'caption',
										}}
										sx={{ m: 0, minWidth: 0 }}
									/>
									<Tooltip title="Conversation actions">
										<IconButton
											aria-expanded={menuThreadId === thread.id}
											aria-haspopup="menu"
											aria-label="Conversation actions"
											className="thread-menu-btn"
											edge="end"
											onClick={(event) => {
												event.stopPropagation()
												event.currentTarget.blur()
												onOpenMenu(event.currentTarget, thread.id)
											}}
											size="small"
											sx={{ color: 'text.secondary', flexShrink: 0, transition: 'opacity 150ms' }}
										>
											<DotsThreeIcon size={18} weight="bold" />
										</IconButton>
									</Tooltip>
								</ListItemButton>
							))}
						</Box>
					))
				)}
			</List>
		</Box>
	)
}
